/**
 * Modern 现代模板 - 详情页
 * 
 * 风格特点：
 * - 顶部大图轮播区
 * - 左侧主内容 + 右侧信息卡片
 * - 底部相似推荐网格
 * - 联系方式悬浮按钮/卡片
 */

import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import {
  getListingDetail,
  getListings,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { useTemplateColors } from "@/hooks/useTemplateColors";

export default function ModernListingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();
  const { siteConfig } = useSiteConfig();
  const colors = useTemplateColors();

  const [listing, setListing] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [relatedListings, setRelatedListings] = useState<ListingItem[]>([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showContact, setShowContact] = useState(false);

  // 加载详情
  useEffect(() => {
    if (!id || siteLoading || !siteId) return;
    setLoading(true);

    getListingDetail(id)
      .then((data) => setListing(data))
      .catch(() => navigate("/category", { replace: true }))
      .finally(() => setLoading(false));
  }, [id, siteId, siteLoading]);

  // 加载相关推荐
  useEffect(() => {
    if (loading || !listing || !siteId) return;

    getListings({ page_size: 4 })
      .then((data) => {
        const filtered = (data.list || []).filter(l => l.id !== id).slice(0, 4);
        setRelatedListings(filtered);
      })
      .catch(console.error);
  }, [loading, listing, id, siteId]);

  if (loading) return <LoadingSkeleton />;

  if (!listing) return null;

  const images = listing.images && listing.images.length > 0 ? listing.images : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* 面包屑 */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-1.5 text-sm">
          <Link to="/" className="text-blue-200 hover:text-white transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-xs text-blue-300" />
          <Link to="/category" className="text-blue-200 hover:text-white transition-colors">分类</Link>
          {listing.category_name && (
            <>
              <i className="ri-arrow-right-s-line text-xs text-blue-300" />
              <span className="text-blue-200 line-clamp-1">{listing.category_name}</span>
            </>
          )}
          <i className="ri-arrow-right-s-line text-xs text-blue-300" />
          <span className="text-white font-medium truncate">{listing.title}</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* ════════ 左侧主内容 ════════ */}
          <div className="flex-1 min-w-0 space-y-6">

            {/* ── 图片展示区 ── */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
              {/* 主图 */}
              <div className="relative w-full h-[320px] md:h-[420px] bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
                {images.length > 0 ? (
                  <img
                    src={images[activeImageIndex]}
                    alt={listing.title}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-6xl text-gray-300" />
                  </div>
                )}

                {/* 图片导航 */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setActiveImageIndex(i => i === 0 ? images.length - 1 : i - 1)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line" />
                    </button>
                    <button
                      onClick={() => setActiveImageIndex(i => i === images.length - 1 ? 0 : i + 1)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line" />
                    </button>

                    {/* 缩略图条 */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 px-3 py-1.5 bg-black/30 backdrop-blur-md rounded-xl">
                      {images.map((img: string, i: number) => (
                        <button
                          key={i}
                          onClick={() => setActiveImageIndex(i)}
                          className={`w-10 h-7 rounded-md overflow-hidden border-2 transition-all cursor-pointer ${
                            activeImageIndex === i ? `${colors.borderActive}` : 'border-transparent opacity-70 hover:opacity-100'
                          }`}
                        >
                          <img src={img} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* 标题 + 元数据 */}
              <div className="p-5 md:p-6">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h1 className="text-lg md:text-2xl font-bold text-gray-800 leading-tight">{listing.title}</h1>
                  {listing.is_pinned && (
                    <span className={`shrink-0 px-2.5 py-1 ${colors.badgeBg} text-white text-xs font-semibold rounded-lg`}>置顶</span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-400">
                  {listing.category_name && (
                    <span className={`px-2 py-0.5 ${colors.bgLight} ${colors.text} rounded-md text-xs font-medium`}>{listing.category_name}</span>
                  )}
                  <span className="flex items-center gap-1"><i className="ri-time-line" />{new Date(listing.created_at).toLocaleDateString('zh-CN')}</span>
                  <span className="flex items-center gap-1"><i className="ri-eye-line" />{listing.view_count} 次浏览</span>
                </div>
              </div>
            </div>

            {/* ── 详细描述 ── */}
            <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100">
              <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <i className={`ri-file-text-line ${colors.text}`} />
                详细介绍
              </h2>
              <div className="prose prose-sm max-w-none prose-gray prose-p:leading-relaxed">
                <p className="text-gray-600 whitespace-pre-wrap leading-relaxed">{listing.content}</p>
              </div>
            </div>
          </div>

          {/* ════════ 右侧信息卡 ════════ */}
          <aside className="lg:w-80 shrink-0 space-y-5">
            {/* 联系人信息 */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100">
              <h3 className="text-sm font-bold text-blue-800 mb-4">联系人</h3>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white shrink-0">
                  <i className="ri-user-line text-xl" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-blue-900 text-sm truncate">{listing.contact_name || '匿名用户'}</p>
                  <p className="text-xs text-blue-500">联系人</p>
                </div>
              </div>
            </div>

            {/* 联系方式卡片 */}
            <div className={`${colors.bgGradient} rounded-2xl p-5 text-white`}>
              <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
                <i className="ri-phone-line" />
                联系方式
              </h3>

              {!showContact ? (
                <button
                  onClick={() => setShowContact(true)}
                  className="w-full py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl font-medium text-sm transition-all cursor-pointer"
                >
                  点击查看联系方式
                </button>
              ) : (
                <div className="space-y-3">
                  {(listing.contact_phone || listing.contact_wechat) ? (
                    <>
                      {listing.contact_phone && (
                        <div className="flex items-center gap-3 py-2.5 px-3 bg-white/10 rounded-xl">
                          <i className="ri-phone-line text-cyan-300" />
                          <a href={`tel:${listing.contact_phone}`} className="text-sm font-medium">{listing.contact_phone}</a>
                        </div>
                      )}
                      {listing.contact_wechat && (
                        <div className="flex items-center gap-3 py-2.5 px-3 bg-white/10 rounded-xl">
                          <i className="ri-wechat-line text-green-300" />
                          <span className="text-sm font-medium">{listing.contact_wechat}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-white/50">暂无联系方式</p>
                  )}
                </div>
              )}

              {/* 公司名称 - 始终显示，无需点击查看联系方式 */}
              {siteConfig && listing.company_name && !siteConfig.hide_company_name && (
                <div className="pt-3 mt-3 border-t border-white/15">
                  <p className="text-xs text-white/50 mb-1">公司名称</p>
                  <p className="text-sm font-medium">{listing.company_name}</p>
                </div>
              )}
            </div>
          </aside>
        </div>

        {/* ════════ 相关推荐 ════════ */}
        {relatedListings.length > 0 && (
          <section className="mt-12">
            <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
              <i className={`ri-thumb-up-line ${colors.text}`} />
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedListings.map(item => (
                <Link
                  key={item.id}
                  to={`/listing/${item.id}`}
                  className={`group bg-white rounded-xl border border-gray-100 ${colors.cardHoverBorder} hover:shadow-md transition-all overflow-hidden`}
                >
                  <div className="w-full h-32 bg-slate-100 overflow-hidden relative">
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt={item.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><i className="ri-image-line text-2xl text-gray-300" /></div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className={`text-sm font-medium text-gray-800 line-clamp-1 ${colors.cardHoverText} transition-colors`}>{item.title}</h3>
                    <p className="text-xs text-gray-400 mt-1">{item.category?.name}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="animate-pulse space-y-6">
          <div className="w-full h-[420px] bg-gray-200 rounded-2xl" />
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 h-48 bg-gray-200 rounded-2xl" />
            <div className="space-y-4">
              <div className="h-28 bg-gray-200 rounded-2xl" />
              <div className="h-36 bg-gray-200 rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
